package er.prateek.crossly.ui.components

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.game
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random

private class ConfettiPiece(
    val startXFraction: Float,
    val angle: Float,
    val speed: Float,
    val spin: Float,
    val widthDp: Float,
    val heightDp: Float,
    val color: Color,
    val delayFraction: Float,
)

/**
 * One-shot celebration burst for the results screen: pieces launch from just
 * below the top of the frame, arc under gravity and fade out. Purely decorative
 * and non-interactive, so it never blocks the buttons underneath.
 *
 * Everything is a single [Canvas] — no per-piece composable — so a 60-piece
 * burst stays cheap on the web targets too.
 */
@Composable
fun ConfettiBurst(
    modifier: Modifier = Modifier,
    pieceCount: Int = 56,
    durationMillis: Int = 2600,
    seed: Int = 0,
) {
    val colors = MaterialTheme.game
    val palette = listOf(
        colors.gold,
        colors.flame,
        colors.accent,
        colors.accentAlt,
        colors.success,
    )

    val pieces = remember(seed, pieceCount) {
        val random = Random(seed * 31 + pieceCount)
        List(pieceCount) {
            ConfettiPiece(
                startXFraction = random.nextFloat(),
                // Fan out from straight-up (-90deg) by up to +-55deg.
                angle = (-90f + (random.nextFloat() - 0.5f) * 110f) * (3.14159f / 180f),
                speed = 0.55f + random.nextFloat() * 0.75f,
                spin = (random.nextFloat() - 0.5f) * 12f,
                widthDp = 6f + random.nextFloat() * 6f,
                heightDp = 9f + random.nextFloat() * 8f,
                color = palette[random.nextInt(palette.size)],
                delayFraction = random.nextFloat() * 0.25f,
            )
        }
    }

    val progress = remember(seed) { Animatable(0f) }
    LaunchedEffect(seed) {
        progress.snapTo(0f)
        progress.animateTo(1f, tween(durationMillis, easing = LinearEasing))
    }

    Canvas(modifier = modifier.fillMaxSize()) {
        val t = progress.value
        if (t >= 1f) return@Canvas

        pieces.forEach { piece ->
            // Each piece runs its own normalised clock so the burst staggers.
            val local = ((t - piece.delayFraction) / (1f - piece.delayFraction)).coerceIn(0f, 1f)
            if (local <= 0f) return@forEach

            val launch = size.height * piece.speed
            val originX = size.width * piece.startXFraction
            val originY = size.height * 0.28f

            val x = originX + cos(piece.angle) * launch * local
            // Ballistic arc: upward impulse minus gravity, both scaled to the frame.
            val y = originY + sin(piece.angle) * launch * local +
                size.height * 1.5f * local * local

            if (y > size.height + 40f) return@forEach

            val alpha = (1f - local * local).coerceIn(0f, 1f)
            val w = piece.widthDp.dp.toPx()
            val h = piece.heightDp.dp.toPx()

            rotate(degrees = piece.spin * local * 360f, pivot = Offset(x, y)) {
                drawRect(
                    color = piece.color.copy(alpha = alpha),
                    topLeft = Offset(x - w / 2f, y - h / 2f),
                    size = Size(w, h),
                )
            }
        }
    }
}

@Composable
fun SparkBurst(
    modifier: Modifier = Modifier,
    pieceCount: Int = 80,
    durationMillis: Int = 2200,
    seed: Int = 1,
) {
    val colors = MaterialTheme.game
    val palette = listOf(
        colors.gold,
        colors.gold,
        colors.flame,
        colors.accent,
        colors.success,
    )
    val pieces = remember(seed, pieceCount) {
        val random = Random(seed * 17 + pieceCount)
        List(pieceCount) {
            ConfettiPiece(
                startXFraction = 0.5f,
                angle = (random.nextFloat() * 360f - 180f) * (3.14159f / 180f),
                speed = 0.35f + random.nextFloat() * 0.85f,
                spin = (random.nextFloat() - 0.5f) * 18f,
                widthDp = 3f + random.nextFloat() * 5f,
                heightDp = 8f + random.nextFloat() * 10f,
                color = palette[random.nextInt(palette.size)],
                delayFraction = random.nextFloat() * 0.18f,
            )
        }
    }
    val progress = remember(seed) { Animatable(0f) }
    LaunchedEffect(seed) {
        progress.snapTo(0f)
        progress.animateTo(1f, tween(durationMillis, easing = LinearEasing))
    }
    Canvas(modifier = modifier.fillMaxSize()) {
        val t = progress.value
        if (t >= 1f) return@Canvas
        pieces.forEach { piece ->
            val local = ((t - piece.delayFraction) / (1f - piece.delayFraction)).coerceIn(0f, 1f)
            if (local <= 0f) return@forEach
            val launch = size.minDimension * piece.speed
            val originX = size.width * 0.5f
            val originY = size.height * 0.38f
            val x = originX + cos(piece.angle) * launch * local
            val y = originY + sin(piece.angle) * launch * local + size.height * 0.35f * local * local
            val alpha = (1f - local * local).coerceIn(0f, 1f)
            val w = piece.widthDp.dp.toPx()
            val h = piece.heightDp.dp.toPx()
            rotate(degrees = piece.spin * local * 360f, pivot = Offset(x, y)) {
                drawRect(
                    color = piece.color.copy(alpha = alpha),
                    topLeft = Offset(x - w / 2f, y - h / 2f),
                    size = Size(w, h),
                )
            }
        }
    }
}
